import React, { useContext, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import AuthHeader from "../../../components/auth/AuthHeader/AuthHeader";
import {
  Box,
  ButtonStyled,
  HeadingStyled,
  HeadingStyledLg,
  SpanStyled,
  TextStyled,
} from "../../../components/styled/Elements";
import ValidatedInput from "../../../components/common/inputFields/ValidatedInput";
import PhoneInputField from "../../../components/common/inputFields/PhoneInput";
import CustomRadioButton from "../../../components/common/radioButtons/CustomRadio";
import { MultiSelectionDropdown } from "../../../components/common/dropdowns/MultiSelectDropdown";
import ValidationController from "../../../components/common/inputFields/ValidationController";
import { AuthContext } from "../../../context/authContext";
import { useUser } from "../../../context/userContext";
import VisitorApi from "../../../services/api/visitorApi";
import { LayoutWrapper } from "../../../layout/ProfileLayout";
import { LeadFormWrapper } from "../enquire/lead-form/style";
import {
  LookingForWrapper,
  SmBanner,
} from "../../../components/profile/lookingForBanner/styled";
import {
  Email,
  Phone,
  SearchPropIcon,
} from "../../../components/styled/AllImages";

const LOOKING_FOR = [
  { value: 1, label: "Buy" },
  { value: 2, label: "Rent" },
  { value: 3, label: "Sale" },
];

const CATEGORIES = [
  { value: 1, label: "Residential" },
  { value: 2, label: "Commercial" },
];

const GeneralRequest = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const { user } = useUser();
  const [cities, setCities] = useState([]);
  const [loadingScope, setLoadingScope] = useState("");
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      inquiry_type: 1,
      property_category: "",
      full_name: "",
      phone_number: "",
      message: "",
      city_id: [],
    },
  });

  const inquiryType = watch("inquiry_type");
  const propertyCategory = watch("property_category");
  const cityIds = watch("city_id") || [];

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login?next=/general-request", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (user?.name) {
      setValue("full_name", user.name);
    }
    if (user?.phone) {
      setValue("phone_number", user.phone);
    }
  }, [user, setValue]);

  useEffect(() => {
    const loadCities = async () => {
      try {
        const { data, code } = await VisitorApi.getCitiesList();
        if (code === 200 && Array.isArray(data)) {
          setCities(
            data.map((city) => ({
              ...city,
              value: city?.id,
              label:
                (i18n.language === "ar" ? city?.name_ar : city?.name_en) ||
                city?.name_en ||
                city?.name_ar ||
                String(city?.id),
            }))
          );
        }
      } catch (error) {}
    };
    loadCities();
  }, [i18n.language]);

  const submitWithScope = (scope) =>
    handleSubmit(async (formData) => {
      if (scope === "cities" && (!Array.isArray(formData.city_id) || formData.city_id.length === 0)) {
        toast.error(t("looking_for_city_required"));
        return;
      }
      const payload = {
        inquiry_type: Number(formData.inquiry_type),
        full_name: formData.full_name,
        phone_number: formData.phone_number,
        message: formData.message,
        scope,
      };
      if (formData.property_category) {
        payload.property_category = Number(formData.property_category);
      }
      if (scope === "cities") {
        payload.city_id = formData.city_id.map((id) => Number(id));
      } else if (Array.isArray(formData.city_id) && formData.city_id.length > 0) {
        payload.city_id = formData.city_id.map((id) => Number(id));
      }
      try {
        setLoadingScope(scope);
        const response = await VisitorApi.createGeneralInquiry(payload);
        if (response?.code === 200) {
          toast.success(response.message || t("general_request_saved_success"));
          navigate("/search-profiles");
          return;
        }
        if (response?.code === 401) {
          navigate("/login?next=/general-request");
          return;
        }
        toast.error(response?.message || t("something_went_wrong"));
      } catch (error) {
        if (error?.code === 401) {
          navigate("/login?next=/general-request");
          return;
        }
        toast.error(error?.message || t("something_went_wrong"));
      } finally {
        setLoadingScope("");
      }
    })();

  if (!isAuthenticated) {
    return null;
  }

  return (
    <LayoutWrapper>
      <AuthHeader />
      <Box className="content-main">
        <PageShell>
          <Box className="heading-container px-5">
            <HeadingStyled
              color="var(--white)"
              size="24px"
              className="mt-3 text-uppercase"
            >
              {t("general_request_screen_title")}
            </HeadingStyled>
          </Box>
          <Box className="content-inner flex-wrap">
            <Row className="lead-content">
              <Col lg={8} md={12}>
                <Box className="form shadow-lg bg-white px-3 px-md-4 rounded-3">
                  <LeadFormWrapper>
                    <Form className="py-3">
                      <Row>
                        <Col lg={12} md={12}>
                          <Box className="field-box border-bottom-line">
                            <HeadingStyled className="label-heading">
                              {t("looking_property")}
                              <span className="staric">*</span>
                            </HeadingStyled>
                            <Box className="active-radio-container d-flex gap-2 flex-wrap">
                              {LOOKING_FOR.map((option) => (
                                <Box key={option.value}>
                                  <CustomRadioButton
                                    className="radio-chip"
                                    name="inquiry_type"
                                    label={t(option.label)}
                                    value={option.value}
                                    checked={Number(inquiryType) === option.value}
                                    onChange={() =>
                                      setValue("inquiry_type", option.value)
                                    }
                                  />
                                </Box>
                              ))}
                            </Box>
                            <input
                              type="hidden"
                              {...register("inquiry_type", { required: true })}
                            />
                            {errors.inquiry_type ? (
                              <div className="error-message">{t("fieldRequired")}</div>
                            ) : null}
                          </Box>
                        </Col>
                        <Col lg={12} md={12}>
                          <Box className="field-box border-bottom-line">
                            <HeadingStyled className="label-heading mb-2">
                              {t("Property_category")}
                            </HeadingStyled>
                            <Box className="active-radio-container d-flex gap-2 flex-wrap">
                              {CATEGORIES.map((option) => (
                                <Box key={option.value}>
                                  <CustomRadioButton
                                    className="radio-chip"
                                    name="property_category"
                                    label={t(option.label)}
                                    value={option.value}
                                    checked={Number(propertyCategory) === option.value}
                                    onChange={() =>
                                      setValue(
                                        "property_category",
                                        Number(propertyCategory) === option.value
                                          ? ""
                                          : option.value
                                      )
                                    }
                                  />
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Col>
                        <Col lg={6} md={12}>
                          <Box className="field-box">
                            <HeadingStyled className="label-heading">
                              {t("Full_Name")}
                              <span className="staric">*</span>
                            </HeadingStyled>
                            <ValidatedInput
                              name="full_name"
                              type="input"
                              fieldType="text"
                              placeholder={"Full_Name_place"}
                              className={"w-100 mb-0 combined flex-wrap p-0"}
                              register={register}
                              errors={errors}
                              validationRules={{ required: true }}
                            />
                          </Box>
                        </Col>
                        <Col lg={6} md={12}>
                          <Box className="field-box">
                            <HeadingStyled className="label-heading">
                              {t("Phone_Number")}
                              <span className="staric">*</span>
                            </HeadingStyled>
                            <PhoneInputField
                              name="phone_number"
                              placeholder={"Phone_Number_place"}
                              register={register}
                              control={control}
                              errors={errors}
                              validationRules={{ required: true }}
                              className={"w-100 mb-0 combined flex-wrap p-0"}
                            />
                          </Box>
                        </Col>
                        <Col lg={12} md={12}>
                          <Box className="field-box border-bottom-line">
                            <HeadingStyled className="label-heading">
                              {t("general_request_city_optional")}
                            </HeadingStyled>
                            <ValidationController
                              name={"city_id"}
                              control={control}
                              errors={errors}
                              validationConfig={{}}
                              renderer={({ field }) => (
                                <MultiSelectionDropdown
                                  name={field.name}
                                  placeholder={t("general_request_city_hint")}
                                  options={cities}
                                  value={field.value || []}
                                  onChange={(selected) => field.onChange(selected)}
                                />
                              )}
                            />
                          </Box>
                        </Col>
                        <Col lg={12} md={12}>
                          <Box className="field-box">
                            <HeadingStyled className="label-heading">
                              {t("general_request_message")}
                              <span className="staric">*</span>
                            </HeadingStyled>
                            <ValidatedInput
                              name="message"
                              type="textarea"
                              fieldType="text"
                              placeholder={"general_request_message"}
                              className={"w-100 mb-0 combined flex-wrap p-0"}
                              register={register}
                              errors={errors}
                              validationRules={{
                                required: true,
                                maxLength: {
                                  value: 1000,
                                  message: "general_request_message_max",
                                },
                              }}
                            />
                          </Box>
                        </Col>
                      </Row>
                      <Box className="d-flex align-items-center gap-2 justify-content-end flex-wrap mt-3 mb-2">
                        {Array.isArray(cityIds) && cityIds.length > 0 ? (
                          <ButtonStyled
                            type="button"
                            label={t("general_request_send_cities")}
                            color="var(--themeColor)"
                            bg="var(--white)"
                            border="var(--themeColor)"
                            isLoading={loadingScope === "cities"}
                            disabled={Boolean(loadingScope)}
                            onClick={() => submitWithScope("cities")}
                          />
                        ) : null}
                        <ButtonStyled
                          type="button"
                          label={t("general_request_send_all")}
                          color="var(--white)"
                          bg="var(--themeColorSecondary)"
                          isLoading={loadingScope === "all"}
                          disabled={Boolean(loadingScope)}
                          onClick={() => submitWithScope("all")}
                        />
                      </Box>
                    </Form>
                  </LeadFormWrapper>
                </Box>
              </Col>
              <Col lg={4} md={12}>
                <SmBanner>
                  <Box className="smBannerInner">
                    <LookingForWrapper>
                      <Box className="lookingForContent">
                        <HeadingStyled size="22px" color="var(--white)">
                          {t("Lookingfor")}
                        </HeadingStyled>
                        <Box className="sRHeadingWrapper">
                          <HeadingStyledLg
                            size="40px"
                            className="sRHeading sRHeadingOne"
                          >
                            {t("SubmitRequest")}
                          </HeadingStyledLg>
                          <HeadingStyledLg
                            size="40px"
                            className="sRHeading sRHeadingSec"
                          >
                            {t("SubmitRequest")}
                          </HeadingStyledLg>
                        </Box>
                        <Box>
                          <Box className="d-flex align-items-center gap-1 mt-2">
                            <Email width={24} height={24} fill="var(--white)" />
                            <SpanStyled color="var(--white)">
                              {t("submit_requirements")}
                            </SpanStyled>
                          </Box>
                          <Box className="d-flex align-items-center gap-1 mt-1">
                            <Phone fill="var(--white)" width={24} height={24} />
                            <SpanStyled color="var(--white)">
                              {t("get_brokers_call")}
                            </SpanStyled>
                          </Box>
                        </Box>
                        <img
                          src={SearchPropIcon}
                          alt="search property icon"
                          className="propertySmBannerIcon"
                        />
                      </Box>
                    </LookingForWrapper>
                  </Box>
                </SmBanner>
              </Col>
            </Row>
          </Box>
        </PageShell>
      </Box>
    </LayoutWrapper>
  );
};

const PageShell = styled.section`
  margin-top: 0px;
  .heading-container {
    position: absolute;
    top: 0;
    z-index: -1;
    height: 150px;
    background-color: var(--themeColor);
    width: 100vw;
    left: 50%;
    transform: translateX(-50%);
  }
  .content-inner {
    position: absolute;
    top: 100px;
  }
  .radio-chip {
    width: auto !important;
  }
  .border-bottom-line {
    border-bottom: 1px solid #e1e4e8;
    padding-bottom: 16px;
  }
  .label-heading {
    font-size: 16px;
  }
  .staric {
    color: #c00;
    margin-inline-start: 4px;
  }
`;

export default GeneralRequest;
